<script lang="ts" setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useAuth } from '@/plugins/auth';
import { useMedia } from '@/plugins/media';
import { useSignalR } from '@/plugins/signalr';
import { useLogging } from '@/plugins/logging';
import { useFirebase } from '@/plugins/firebase';
import Account from '@/views/partials/layout/Account.vue';
import Notifications from '@/views/partials/layout/Notifications.vue';
import Assistant from '@/views/partials/layout/Assistant.vue';
import Breadcrumbs from '@/views/partials/navigation/Breadcrumbs.vue';
import Menu from '@/views/partials/navigation/Menu.vue';
import PushNotifications from "@/views/partials/layout/PushNotifications.vue";
import NotificationsService, { ChannelType } from '@/modules/core/common/services/NotificationsService';
import Omnibox from "@/views/partials/layout/Omnibox.vue";
import { onClickOutside } from '@vueuse/core';
import { useThemeStore } from '@/store/theme';

defineOptions({
    name: 'default-layout'
});

const media = useMedia();
const { $auth } = useAuth();
const { $log } = useLogging();
const { channel } = useSignalR();
const { $firebase } = useFirebase();
const server = channel('authentication');
const channels = ref<ChannelType[]>([]);

const sidebar = ref(null);
const collapsed = ref(false);
const hover = ref(false);
const oneSignalEnabled = computed(() =>
{
    return !!(import.meta.env.VITE_APP_ONESIGNAL && import.meta.env.VITE_APP_ONESIGNAL.length);
});

onClickOutside(sidebar, () =>
{
    if (media.mobile())
    {
        collapsed.value = false;
    }
});

const themeStore = useThemeStore();
const changeTheme = (): void =>
{
    themeStore.changeTheme();
};

server.on.logout = async (sessionId: number): Promise<void> =>
{
    const session = Number($auth.claims()["sid"] || 0);

    if (sessionId == null || sessionId == session)
    {
        $auth.logout();
    }
};

const toggleMenu = (value?: boolean): void =>
{
    if (typeof value === 'boolean')
        collapsed.value = value;
    else
        collapsed.value = !collapsed.value;
};

const loadChannels = async (): Promise<void> =>
{
    try
    {
        channels.value = await NotificationsService.getChannels();
    }
    catch (ex)
    {
        channels.value = [];
        $log.debug(ex);
    }
};

const initFirebase = async (): Promise<void> =>
{
    try
    {
        const token = await $firebase.init();

        if (token)
        {
            await NotificationsService.setFirebaseToken(token);
        }
    }
    catch (ex)
    {
        $log.debug(ex);
    }
};

onBeforeMount(async (): Promise<void> =>
{
    await loadChannels();
    await initFirebase();
});
</script>

<template>
    <main id="layout" class="d-flex" :class="{'desktop': desktop, 'laptop': laptop, 'mobile': mobile, 'phone': phone, [mq]: true, 'collapsed': collapsed, 'hover': hover}">
        <nav id="sidebar" ref="sidebar" @mouseenter="hover = true" @mouseleave="hover = false">
            <Menu :collapsed="collapsed" :hover="hover || mobile" @toggle-menu="toggleMenu"></Menu>
        </nav>
        <section id="content">
            <header>
                <nav class="navbar bg-body align-items-stretch">
                    <button type="button" class="navbar-toggler toggle-menu" @click="collapsed = !collapsed">
                        <i class="far fa-bars"></i>
                    </button>
                    <Omnibox></Omnibox>
                    <button type="button" class="navbar-toggler" @click="changeTheme">
                        <i class="far fa-fw fa-circle-half-stroke"></i>
                    </button>
                    <Assistant></Assistant>
                    <Notifications></Notifications>
                    <Account></Account>
                </nav>
            </header>
            <section id="view" class="bg-body-tertiary position-relative">
                <Breadcrumbs></Breadcrumbs>
                <div class="scroll d-flex flex-column" data-name="wrapper">
                    <div class="wrapper flex-fill d-flex flex-column">
                        <RouterView />
                    </div>
                </div>
            </section>
        </section>
    </main>
    <PushNotifications v-if="oneSignalEnabled" />
</template>

<style lang="scss">
#layout {
    --ideo-nav-width: var(--ideo-nav-width-default);
    --ideo-view-padding-size: 1rem;
    --ideo-view-outer-height: calc(100dvh - var(--ideo-header-height));
    --ideo-view-inner-height: calc(var(--ideo-view-outer-height) - var(--ideo-breadcrumbs-height));

    height: 100dvh;

    #sidebar {
        position: fixed;
        z-index: 10001;
        height: 100%;
        width: var(--ideo-nav-width);
        overflow: hidden;
        transition: var(--ideo-transition);
        background-color: var(--ideo-nav-bg);
        color: var(--ideo-nav-color);

        > header {
            height: var(--ideo-header-height);
        }
        > section {
            height: calc(100dvh - var(--ideo-header-height) - var(--ideo-footer-height));
        }
        > footer {
            height: var(--ideo-footer-height);
            width: var(--ideo-nav-width);
        }
    }
    #content {
        height: 100%;
        width: calc(100dvw - var(--ideo-nav-width));
        margin-left: var(--ideo-nav-width);
        transition: var(--ideo-transition);

        > header {
            height: var(--ideo-header-height);

            > .navbar {
                padding: 0px;
                border-bottom: 1px solid var(--bs-border-color);
                height: calc(var(--ideo-header-height) - 1px);
            }
        }
        > #view {
            overflow: hidden;
            height: var(--ideo-view-outer-height);

            > .scroll {
                height: calc(var(--ideo-view-inner-height));

                > .wrapper {
                    padding: var(--ideo-view-padding-size);
                }
            }
        }
    }

    .navbar-toggler {
        height: var(--ideo-header-height);
        padding: 0 14px;
        border: none;
        border-radius: 0;

        &:hover, &.active i {
            color: var(--bs-primary)
        }

        &.toggle-menu {
            padding: 0 16px 0 22px;
        }
    }

    &.collapsed:not(.mobile) {
        --ideo-nav-width: var(--ideo-nav-width-collapsed);

        #sidebar {
            > section {
                height: calc(100dvh - var(--ideo-header-height));
            }
            > footer {
                display: none;
                width: var(--ideo-nav-width-default);
            }
        }
        #content {
            margin-left: var(--ideo-nav-width-collapsed);
        }

        &.hover {
            #sidebar {
                width: var(--ideo-nav-width-default);

                > section {
                    height: calc(100dvh - var(--ideo-header-height) - var(--ideo-footer-height));
                }
                > footer {
                    display: block;
                }
            }
        }
    }

    &.mobile {
        #sidebar {
            visibility: hidden;
            width: 0;
        }
        #content {
            width: 100%;
            margin-left: 0;
        }

        &.collapsed {
            #sidebar {
                visibility: visible;
                width: var(--ideo-nav-width-default);
            }
        }
    }
}
</style>
